import React, { useState, useEffect } from "react";
import { Handshake, Shield, Gavel, Zap, Coins, Bot, Lock, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { PROVIDER_MODELS } from "../components/dispute/theme";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function AdminConfig() {
  const navigate = useNavigate();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth("/api/ai/admin/cache-config");

  // Platform-wide "general judge" — the model that renders verdicts on
  // multi-provider cases. Defaults to Gemini until an admin sets it.
  const [judgeProvider, setJudgeProvider] = useState("gemini");
  const [judgeModel, setJudgeModel] = useState("gemini-3.1-flash-lite");
  const [judgeSaving, setJudgeSaving] = useState(false);
  const [judgeSaved, setJudgeSaved] = useState(false);
  const [judgeError, setJudgeError] = useState(null);
  // Per-provider selectable models (admin-editable, applies platform-wide).
  // Seeded with the bundled defaults so the judge dropdowns work before the
  // config request returns. `modelDrafts` holds the editable textarea text.
  const [providerModels, setProviderModels] = useState(PROVIDER_MODELS);
  const [modelDrafts, setModelDrafts] = useState({});
  const [modelDefaults, setModelDefaults] = useState({}); // provider → default model id
  const [modelsSaving, setModelsSaving] = useState(false);
  const [modelsSaved, setModelsSaved] = useState(false);
  const [modelsError, setModelsError] = useState(null);
  const [modelsValidating, setModelsValidating] = useState(false);
  const [validation, setValidation] = useState({}); // provider → { checked, error, valid, unknown }
  // Anthropic prompt-cache toggle (platform-wide). Defaults to on.
  const [cacheAnthropic, setCacheAnthropic] = useState(true);
  const [cacheSaving, setCacheSaving] = useState(false);
  const [cacheSaved, setCacheSaved] = useState(false);
  const [cacheError, setCacheError] = useState(null);
  // Staking toggle (platform-wide). Defaults to on.
  const [stakingEnabled, setStakingEnabled] = useState(true);
  const [stakingSaving, setStakingSaving] = useState(false);
  const [stakingSaved, setStakingSaved] = useState(false);
  const [stakingError, setStakingError] = useState(null);
  // Filing fee (platform-wide), held as a dollars string while editing.
  const [feeInput, setFeeInput] = useState("");
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeSaved, setFeeSaved] = useState(false);
  const [feeError, setFeeError] = useState(null);
  // Public access toggle (platform-wide). Defaults to on.
  // Stripe test/live switch. `stripeModes` reports which key pairs the server
  // actually has, so an unusable mode can be disabled rather than failing later.
  const [stripeMode, setStripeMode] = useState(null);
  const [stripeModes, setStripeModes] = useState({});
  const [stripeModeSaving, setStripeModeSaving] = useState(false);
  const [stripeModeSaved, setStripeModeSaved] = useState(false);
  const [stripeModeError, setStripeModeError] = useState(null);
  const [publicAccessEnabled, setPublicAccessEnabled] = useState(true);
  const [publicAccessSaving, setPublicAccessSaving] = useState(false);
  const [publicAccessSaved, setPublicAccessSaved] = useState(false);
  const [publicAccessError, setPublicAccessError] = useState(null);

  // Load the current general judge once authenticated (public endpoint).
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/judge-config`)
      .then((r) => r.json())
      .then((d) => {
        const p = d.provider || "gemini";
        setJudgeProvider(p);
        setJudgeModel(d.model || PROVIDER_MODELS[p]?.[0] || "gemini-3.1-flash-lite");
      })
      .catch(() => {});
  }, [authHeader]);

  // Load the admin-configured selectable models per provider.
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/provider-models`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => {
        if (!d.models) return;
        setProviderModels(d.models);
        setModelDefaults(d.defaults || {});
        setModelDrafts(
          Object.fromEntries(Object.entries(d.models).map(([p, list]) => [p, list.join("\n")]))
        );
      })
      .catch(() => {});
  }, [authHeader]);

  // Load the Anthropic cache toggle once authenticated (admin endpoint).
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/cache-config`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => setCacheAnthropic(d.anthropic !== false))
      .catch(() => {});
  }, [authHeader]);

  // Load the staking toggle once authenticated.
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/staking-config`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => setStakingEnabled(d.enabled !== false))
      .catch(() => {});
  }, [authHeader]);

  // Load the filing fee once authenticated.
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/fee-config`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => {
        if (Number.isFinite(d?.cents)) setFeeInput((d.cents / 100).toFixed(2));
      })
      .catch(() => {});
  }, [authHeader]);

  // Load the public access toggle once authenticated.
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/public-access-config`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => setPublicAccessEnabled(d.enabled !== false))
      .catch(() => {});
  }, [authHeader]);

  // Load the Stripe test/live switch (and which modes have keys) once authenticated.
  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/stripe-mode`, { headers: { Authorization: authHeader } })
      .then((r) => r.json())
      .then((d) => {
        if (d?.mode) setStripeMode(d.mode);
        if (d?.modes) setStripeModes(d.modes);
      })
      .catch(() => {});
  }, [authHeader]);

  // Switch Stripe between test and live. The server verifies the target mode's
  // key against the Stripe API before saving, so a rejection here means the key
  // is genuinely missing/invalid — revert the UI and show why.
  const saveStripeMode = async (next) => {
    const prev = stripeMode;
    setStripeMode(next);
    setStripeModeSaving(true);
    setStripeModeError(null);
    setStripeModeSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/stripe-mode`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ mode: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStripeModeSaved(true);
      setTimeout(() => setStripeModeSaved(false), 2500);
    } catch (err) {
      setStripeModeError(err.message);
      setStripeMode(prev);
    } finally {
      setStripeModeSaving(false);
    }
  };

  // Flip the toggle and persist immediately; revert on failure.
  const saveCache = async (next) => {
    setCacheAnthropic(next);
    setCacheSaving(true);
    setCacheError(null);
    setCacheSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/cache-config`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ anthropic: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setCacheSaved(true);
      setTimeout(() => setCacheSaved(false), 2500);
    } catch (err) {
      setCacheError(err.message);
      setCacheAnthropic(!next);
    } finally {
      setCacheSaving(false);
    }
  };

  // Persist the filing fee. The input is in dollars; the API takes cents.
  const saveFee = async () => {
    const dollars = parseFloat(String(feeInput).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(dollars) || dollars < 0) {
      setFeeError("Enter a valid amount, e.g. 5.99");
      return;
    }
    const cents = Math.round(dollars * 100);
    setFeeSaving(true);
    setFeeError(null);
    setFeeSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/fee-config`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ cents }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setFeeInput((cents / 100).toFixed(2));
      setFeeSaved(true);
      setTimeout(() => setFeeSaved(false), 2500);
    } catch (err) {
      setFeeError(err.message);
    } finally {
      setFeeSaving(false);
    }
  };

  const saveStaking = async (next) => {
    setStakingEnabled(next);
    setStakingSaving(true);
    setStakingError(null);
    setStakingSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/staking-config`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStakingSaved(true);
      setTimeout(() => setStakingSaved(false), 2500);
    } catch (err) {
      setStakingError(err.message);
      setStakingEnabled(!next);
    } finally {
      setStakingSaving(false);
    }
  };

  const savePublicAccess = async (next) => {
    setPublicAccessEnabled(next);
    setPublicAccessSaving(true);
    setPublicAccessError(null);
    setPublicAccessSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/public-access-config`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPublicAccessSaved(true);
      setTimeout(() => setPublicAccessSaved(false), 2500);
    } catch (err) {
      setPublicAccessError(err.message);
      setPublicAccessEnabled(!next);
    } finally {
      setPublicAccessSaving(false);
    }
  };

  const handleJudgeProviderChange = (p) => {
    setJudgeProvider(p);
    setJudgeModel(providerModels[p]?.[0] || "");
    setJudgeSaved(false);
  };

  const saveJudge = async () => {
    setJudgeSaving(true);
    setJudgeError(null);
    setJudgeSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/judge-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ provider: judgeProvider, model: judgeModel }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setJudgeSaved(true);
      setTimeout(() => setJudgeSaved(false), 2500);
    } catch (err) {
      setJudgeError(err.message);
    } finally {
      setJudgeSaving(false);
    }
  };

  // Split each provider's textarea (newlines or commas) → trimmed id list.
  // A provider's current (possibly unsaved) model ids, parsed from its textarea.
  const draftModelList = (p) =>
    (modelDrafts[p] ?? (providerModels[p] || []).join("\n"))
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const buildModelsPayload = () => {
    const payload = {};
    for (const p of Object.keys(providerModels)) {
      payload[p] = draftModelList(p);
    }
    return payload;
  };

  const validateProviderModels = async () => {
    setModelsValidating(true);
    setModelsError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/provider-models/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ models: buildModelsPayload() }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setValidation(data.results || {});
    } catch (err) {
      setModelsError(err.message);
    } finally {
      setModelsValidating(false);
    }
  };

  const saveProviderModels = async () => {
    setModelsSaving(true);
    setModelsError(null);
    setModelsSaved(false);
    try {
      const payload = buildModelsPayload();
      const res = await fetch(`${API_BASE}/api/ai/admin/provider-models`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ models: payload, defaults: modelDefaults }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.models) {
        setProviderModels(data.models);
        setModelDefaults(data.defaults || {});
        setModelDrafts(
          Object.fromEntries(Object.entries(data.models).map(([p, list]) => [p, list.join("\n")]))
        );
        // If the judge's model was dropped from its provider's list, snap to the first.
        const judgeList = data.models[judgeProvider];
        if (judgeList && judgeList.length && !judgeList.includes(judgeModel)) {
          setJudgeModel(judgeList[0]);
        }
      }
      setModelsSaved(true);
      setTimeout(() => setModelsSaved(false), 2500);
    } catch (err) {
      setModelsError(err.message);
    } finally {
      setModelsSaving(false);
    }
  };

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input
              type="text"
              autoComplete="username"
              placeholder="Admin username"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              style={styles.loginInput}
              autoFocus
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin password"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              style={styles.loginInput}
            />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button
              type="button"
              onClick={() => navigate("/submit-dispute")}
              style={styles.loginGhost}
            >Back to dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader active="config" onLogout={handleAdminLogout} />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>Config (admin)</h1>
            <p style={styles.subtitle}>Platform-wide settings for AI panels, caching, and staking.</p>
          </div>
        </div>

        {/* ── General AI judge selector (applies platform-wide) ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Gavel size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>General AI judge</h2>
              <p style={styles.judgeCardSub}>
                Renders the verdict on multi-provider cases. Defaults to Gemini if unset.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            <select
              value={judgeProvider}
              onChange={(e) => handleJudgeProviderChange(e.target.value)}
              style={styles.judgeSelect}
              aria-label="Judge provider"
            >
              {Object.keys(providerModels).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={judgeModel}
              onChange={(e) => { setJudgeModel(e.target.value); setJudgeSaved(false); }}
              style={styles.judgeSelect}
              aria-label="Judge model"
            >
              {(providerModels[judgeProvider] || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={saveJudge}
              disabled={judgeSaving}
              style={{ ...styles.judgeSaveBtn, ...(judgeSaving ? styles.judgeSaveBtnDisabled : {}) }}
            >
              {judgeSaving ? "Saving…" : "Save judge"}
            </button>
            {judgeSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {judgeError && <span style={styles.judgeErrMsg}>{judgeError}</span>}
          </div>
        </div>

        {/* ── Selectable models per provider (applies platform-wide) ── */}
        <div style={styles.modelsCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Bot size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Provider models</h2>
              <p style={styles.judgeCardSub}>
                The models each AI provider may be run with — shown in the dispute form and the judge picker above. One model ID per line. The <strong>Default</strong> dropdown sets which model is pre-selected for that provider. Clearing a provider restores its built-in defaults.
              </p>
            </div>
          </div>
          <div style={styles.modelsGrid}>
            {Object.keys(providerModels).map((p) => (
              <div key={p} style={styles.modelsCol}>
                <label style={styles.modelsLabel}>{p}</label>
                <textarea
                  style={styles.modelsTextarea}
                  value={modelDrafts[p] ?? (providerModels[p] || []).join("\n")}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModelDrafts((prev) => ({ ...prev, [p]: v }));
                    setModelsSaved(false);
                    // Edited list → the last validation result for this provider is stale.
                    setValidation((prev) => { const n = { ...prev }; delete n[p]; return n; });
                  }}
                  rows={5}
                  spellCheck={false}
                  placeholder="one-model-id-per-line"
                />
                {validation[p] && (
                  !validation[p].checked ? (
                    <span style={styles.modelsWarn}>⚠ couldn’t check: {validation[p].error}</span>
                  ) : validation[p].unknown.length === 0 ? (
                    <span style={styles.modelsOk}>✓ all {validation[p].valid.length} recognized by API</span>
                  ) : (
                    <span style={styles.modelsWarn}>⚠ not on API: {validation[p].unknown.join(", ")}</span>
                  )
                )}
                <label style={styles.modelsDefaultRow}>
                  <span style={styles.modelsDefaultLabel}>Default</span>
                  <select
                    style={styles.modelsDefaultSelect}
                    disabled={draftModelList(p).length === 0}
                    value={draftModelList(p).includes(modelDefaults[p]) ? modelDefaults[p] : (draftModelList(p)[0] || "")}
                    onChange={(e) => {
                      setModelDefaults((prev) => ({ ...prev, [p]: e.target.value }));
                      setModelsSaved(false);
                    }}
                  >
                    {draftModelList(p).length === 0 && <option value="">—</option>}
                    {draftModelList(p).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
              </div>
            ))}
          </div>
          <div style={styles.judgeControls}>
            <button
              type="button"
              onClick={validateProviderModels}
              disabled={modelsValidating}
              style={{ ...styles.modelsValidateBtn, ...(modelsValidating ? styles.judgeSaveBtnDisabled : {}) }}
            >
              {modelsValidating ? "Checking…" : "Validate against API"}
            </button>
            <button
              type="button"
              onClick={saveProviderModels}
              disabled={modelsSaving}
              style={{ ...styles.judgeSaveBtn, ...(modelsSaving ? styles.judgeSaveBtnDisabled : {}) }}
            >
              {modelsSaving ? "Saving…" : "Save models"}
            </button>
            {modelsSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {modelsError && <span style={styles.judgeErrMsg}>{modelsError}</span>}
          </div>
        </div>

        {/* ── Prompt caching (Anthropic) ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Zap size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Prompt caching</h2>
              <p style={styles.judgeCardSub}>
                Caches the constant rulebook on Anthropic for cheaper, faster runs. OpenAI &amp; Gemini cache automatically and can’t be toggled.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            <label style={styles.cacheToggle} title="Enable Anthropic prompt caching">
              <input
                type="checkbox"
                checked={cacheAnthropic}
                disabled={cacheSaving}
                onChange={(e) => saveCache(e.target.checked)}
              />
              <span>Anthropic caching {cacheAnthropic ? "on" : "off"}</span>
            </label>
            {cacheSaving && <span style={styles.judgeCardSub}>Saving…</span>}
            {cacheSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {cacheError && <span style={styles.judgeErrMsg}>{cacheError}</span>}
          </div>
        </div>

        {/* ── Filing fee ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Coins size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Submission fee</h2>
              <p style={styles.judgeCardSub}>
                Charged to file a dispute with a three-Neutral panel, and shown in the pricing copy on
                the submit and checkout pages. Applies to disputes created after the change — a
                dispute's fee is frozen when it's drafted. Stripe requires at least $0.50; 0 makes
                filing free.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            <input
              type="text"
              inputMode="decimal"
              value={feeInput}
              onChange={(e) => { setFeeInput(e.target.value); setFeeSaved(false); }}
              style={{ ...styles.judgeSelect, width: 110 }}
              placeholder="5.99"
              aria-label="Filing fee in dollars"
            />
            <button
              type="button"
              onClick={saveFee}
              disabled={feeSaving}
              style={{ ...styles.judgeSaveBtn, ...(feeSaving ? styles.judgeSaveBtnDisabled : {}) }}
            >
              {feeSaving ? "Saving…" : "Save fee"}
            </button>
            {feeSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {feeError && <span style={styles.judgeErrMsg}>{feeError}</span>}
          </div>
        </div>

        {/* ── Staking toggle ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Coins size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Staking</h2>
              <p style={styles.judgeCardSub}>
                When enabled, filers can optionally place a monetary stake that the opposing party must match. Winner takes 75% of the pot.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            <label style={styles.cacheToggle} title="Enable staking on disputes">
              <input
                type="checkbox"
                checked={stakingEnabled}
                disabled={stakingSaving}
                onChange={(e) => saveStaking(e.target.checked)}
              />
              <span>Staking {stakingEnabled ? "enabled" : "disabled"}</span>
            </label>
            {stakingSaving && <span style={styles.judgeCardSub}>Saving…</span>}
            {stakingSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {stakingError && <span style={styles.judgeErrMsg}>{stakingError}</span>}
          </div>
        </div>

        {/* ── Stripe test/live mode ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <CreditCard size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Stripe mode</h2>
              <p style={styles.judgeCardSub}>
                Which Stripe keys the site takes payments with. <strong>Test</strong> accepts only
                test cards (4242…) and moves no money; <strong>live</strong> charges real cards.
                Applies to new filings straight away — no rebuild or restart. Cases already paid for
                keep settling with the keys they were filed under, so switching never strands money
                in flight. A test-mode site shows a banner on every page.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            {stripeMode === null ? (
              <span style={styles.judgeCardSub}>Loading…</span>
            ) : (
              <div style={styles.stripeModeRow}>
                {["test", "live"].map((m) => {
                  const cfg = stripeModes[m] || {};
                  const usable = cfg.secretKey && cfg.publishableKey;
                  const active = stripeMode === m;
                  const missing = [
                    !cfg.secretKey && "secret key",
                    !cfg.publishableKey && "publishable key",
                  ].filter(Boolean);
                  return (
                    <button
                      key={m}
                      type="button"
                      disabled={stripeModeSaving || (!usable && !active)}
                      onClick={() => !active && saveStripeMode(m)}
                      title={usable ? `Use Stripe ${m} keys` : `Missing ${missing.join(" + ")} for ${m} mode`}
                      style={{
                        ...styles.stripeModeBtn,
                        ...(active ? (m === "live" ? styles.stripeModeBtnLive : styles.stripeModeBtnTest) : {}),
                        ...(!usable && !active ? styles.stripeModeBtnDisabled : {}),
                      }}
                    >
                      {m === "live" ? "Live — real charges" : "Test — no real charges"}
                      {!usable && <span style={styles.stripeModeMissing}>no {missing.join(" / ")}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {stripeModeSaving && <span style={styles.judgeCardSub}>Switching…</span>}
            {stripeModeSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {stripeModeError && <span style={styles.judgeErrMsg}>{stripeModeError}</span>}
          </div>
        </div>

        {/* ── Public access toggle ── */}
        <div style={styles.judgeCard}>
          <div style={styles.judgeCardLeft}>
            <span style={styles.judgeIconWrap}>
              <Lock size={18} strokeWidth={1.9} />
            </span>
            <div>
              <h2 style={styles.judgeCardTitle}>Public access</h2>
              <p style={styles.judgeCardSub}>
                When disabled, the "Log in" link is hidden site-wide and self-service registration is
                blocked. Admins, enabled beta users, and anyone invited as a participant on a beta
                user's dispute can still register or log in — everyone else gets an invite-only
                message. The login page itself stays reachable at /login; only the nav link is hidden.
              </p>
            </div>
          </div>
          <div style={styles.judgeControls}>
            <label style={styles.cacheToggle} title="Open self-service registration and login site-wide">
              <input
                type="checkbox"
                checked={publicAccessEnabled}
                disabled={publicAccessSaving}
                onChange={(e) => savePublicAccess(e.target.checked)}
              />
              <span>Public access {publicAccessEnabled ? "open" : "invite-only"}</span>
            </label>
            {publicAccessSaving && <span style={styles.judgeCardSub}>Saving…</span>}
            {publicAccessSaved && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
            {publicAccessError && <span style={styles.judgeErrMsg}>{publicAccessError}</span>}
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: {
    fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0",
    letterSpacing: "-0.3px", color: COLORS.ink,
  },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box",
    background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: {
    background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 6,
    padding: "8px 10px", fontSize: 12,
  },
  loginBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer",
  },
  loginGhost: {
    background: "transparent", color: COLORS.inkSoft,
    border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer",
  },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 1280, margin: "0 auto" },

  pageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 16, marginBottom: 24,
  },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },

  // ── General judge selector ───────────────────────────────────────────────
  judgeCard: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 16,
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, padding: "16px 20px", marginBottom: 20,
  },
  judgeCardLeft: { display: "flex", alignItems: "center", gap: 14, minWidth: 0 },
  judgeIconWrap: {
    width: 36, height: 36, flexShrink: 0, borderRadius: 8,
    background: COLORS.bgAlt, color: COLORS.dark,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  judgeCardTitle: { fontFamily: SERIF, fontSize: 18, fontWeight: 600, margin: 0, letterSpacing: "-0.2px" },
  judgeCardSub: { fontSize: 12, color: COLORS.inkMuted, margin: "3px 0 0 0" },
  judgeControls: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  judgeSelect: {
    background: COLORS.bg, border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "8px 12px", fontSize: 13, fontFamily: SANS, color: COLORS.ink,
    outline: "none", textTransform: "capitalize", cursor: "pointer",
  },
  judgeSaveBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontWeight: 600, fontFamily: SANS, cursor: "pointer",
  },
  judgeSaveBtnDisabled: { background: "#9aa3ad", cursor: "not-allowed" },
  judgeSavedMsg: { fontSize: 13, color: "#1f6f43", fontWeight: 600 },
  modelsCard: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, padding: "16px 20px", marginBottom: 20,
    display: "flex", flexDirection: "column", gap: 16,
  },
  modelsGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14,
  },
  modelsCol: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  modelsLabel: {
    fontSize: 12, fontWeight: 700, textTransform: "capitalize",
    color: COLORS.inkSoft, letterSpacing: 0.3,
  },
  modelsTextarea: {
    width: "100%", boxSizing: "border-box", resize: "vertical",
    background: COLORS.bg, border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "8px 10px", fontSize: 12, fontFamily: MONO, color: COLORS.ink,
    outline: "none", lineHeight: 1.5,
  },
  modelsValidateBtn: {
    background: "transparent", color: COLORS.dark, border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600,
    fontFamily: SANS, cursor: "pointer",
  },
  modelsOk: { fontSize: 11, color: "#1f6f43", fontWeight: 600, lineHeight: 1.4 },
  modelsWarn: { fontSize: 11, color: "#991b1b", fontWeight: 600, lineHeight: 1.4, wordBreak: "break-word" },
  modelsDefaultRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 2 },
  modelsDefaultLabel: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
    color: COLORS.inkMuted, flexShrink: 0,
  },
  modelsDefaultSelect: {
    flex: 1, minWidth: 0, background: COLORS.bg, border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 8, padding: "6px 8px", fontSize: 12, fontFamily: MONO, color: COLORS.ink,
    outline: "none", cursor: "pointer",
  },
  cacheToggle: {
    display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 600, color: COLORS.ink,
    background: COLORS.bg, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, padding: "8px 12px",
  },
  judgeErrMsg: { fontSize: 12, color: "#991b1b" },

  // ── Stripe mode switch ───────────────────────────────────────────────────
  stripeModeRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  stripeModeBtn: {
    display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
    padding: "8px 14px", borderRadius: 8, cursor: "pointer",
    border: `1px solid ${COLORS.border}`, background: COLORS.card, color: COLORS.inkMuted,
    fontFamily: SANS, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
    transition: "all 0.15s",
  },
  stripeModeBtnTest: {
    background: "#FEF3C7", borderColor: "#FCD34D", color: "#78350F",
  },
  stripeModeBtnLive: {
    background: "#DCFCE7", borderColor: "#86EFAC", color: "#14532D",
  },
  stripeModeBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  stripeModeMissing: { fontSize: 10, fontWeight: 500, letterSpacing: 0.3, textTransform: "uppercase" },
};
